import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { prewarmAdminApi } from "@/services/prewarmService";
import { ROUTE_PATHS } from "@/routes/routePaths";

/** Routes that directly consume the Routes Information Admin API */
const ROUTES_REQUIRING_ADMIN_API = [
  ROUTE_PATHS.FIND_RIDES,
  ROUTE_PATHS.RIDE_REQUESTS,
  ROUTE_PATHS.INTERCITY_ROUTES,
  "/find-rides",
  "/waiting-guests",
  "/available-guests",
];

function isRoutesDiscoveryPath(pathname: string): boolean {
  const norm = pathname.toLowerCase();
  return ROUTES_REQUIRING_ADMIN_API.some((prefix) => norm.startsWith(prefix.toLowerCase()));
}

type WindowWithIdle = Window & {
  requestIdleCallback: (cb: () => void, options?: { timeout: number }) => number;
  cancelIdleCallback: (id: number) => void;
};

/**
 * Schedules a callback when the browser main thread is idle,
 * falling back to setTimeout for older browsers.
 */
function scheduleIdleTask(callback: () => void, timeoutMs = 3000): () => void {
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    const win = window as unknown as WindowWithIdle;
    const handle = win.requestIdleCallback(callback, { timeout: timeoutMs });
    return () => win.cancelIdleCallback(handle);
  }

  const timer = setTimeout(callback, 1500);
  return () => clearTimeout(timer);
}

/**
 * Centralized, non-blocking pre-warm synchronization component.
 * Mounts at the application root (App.tsx) alongside SeoRouteSync and StructuredDataSync.
 *
 * Responsibilities:
 * 1. Background Warmup: Pings ACA Admin API when browser is idle on Landing page.
 * 2. Intent Routing: Refreshes warm state upon entering routes discovery paths (persisted via sessionStorage).
 * 3. Tab Visibility: Resumes container warm-up if the user returns to an active tab after cooldown.
 */
export function BackendPrewarmSync() {
  const { pathname } = useLocation();
  const isInitialMount = useRef(true);

  // Unified Route & Idle Warmup:
  useEffect(() => {
    // 1. Initial mount on home page: run when browser is idle to protect FCP & LCP
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (pathname === "/") {
        return scheduleIdleTask(prewarmAdminApi);
      }
    }

    // 2. Route discovery paths: warm Admin API (debounced by 5m session cooldown)
    if (isRoutesDiscoveryPath(pathname)) {
      prewarmAdminApi();
    }
  }, [pathname]);

  // 3. Tab Visibility Resume: When user switches back to tab after being away
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && isRoutesDiscoveryPath(window.location.pathname)) {
        prewarmAdminApi();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  return null;
}
